import React, {useEffect, useState} from 'react'
import Chart from "react-apexcharts";
import {
  addDays, addMonths,
  addYears,
  differenceInCalendarMonths,
  differenceInMonths, endOfMonth, endOfYear, startOfDay,
  startOfToday,
  startOfYear
} from "date-fns";
import cloneDeep from "lodash.clonedeep";
import {
  getDateToday, getISODateFromDate,
  isoToLocalDate,
  numberToMoneyFormat, numberToPercentFormat
} from "../../../../stores/StoreUtilities";
import {Box, Button, FormLabel, Grid, Typography, useTheme} from "@mui/material";
import {globalColors} from "../../../../styles/globalStyles";
import SocialSecurityStrategyChange, {ISocialSecurityBenefit} from "../../../../model/changes/SocialSecurityStrategyChange";
import Person from "../../../../model/Person";
import {useResources} from "../../../../stores/ResourceProvider";
import ProgressButton from "../../../../components/form/ProgressButton";

const startYear = 2000
const years = 51

const labels: string[] = []
const netWorth: number[] = []
for (let y = 0; y < years; y++) {
  labels.push((startYear + y).toString())
  netWorth.push(200000 + (10000 * y))
}

const defaultOptions = {
  chart: {
    id: "area-datetime",
    type: 'area',
    height: 350,
    width: "100%",
    zoom: {
      enabled: false,
      autoScaleYaxis: true
    },
    animations: {
      enabled: false
    },
    events: {
      mounted: (chart: any) => {
        chart.windowResizeHandler();
      },
    },
    toolbar: {
      show: false,
      tools: {
        download: false
      }
    }
  },
  dataLabels: {
    enabled: false
  },
  legend: {
    show: false,
    position: 'bottom',
    horizontalAlign: 'left'
  },
  markers: {
    size: 0,
    style: 'hollow',
  },
  xaxis: {
    type: 'datetime',
    min: new Date('01 Jan 2020').getTime(),
    max: 0,
    tickAmount: 6,
  },
  yaxis: {
    labels: {
      formatter: (val: number) => {
        return numberToMoneyFormat(val, 0)
        // if (val < 1000000) {
        //   return `$${val / 1000}K`
        // } else {
        //   return `$${val / 1000000}M`
        // }
      }
    },
    min: 0,
    max: 10000,
    forceNiceScale: false
  },
  tooltip: {
    x: {
      format: 'dd MMM yyyy'
    }
  },
  colors: [globalColors.primary, globalColors.secondary],
  fill: {
    type: 'gradient',
    gradient: {
      shadeIntensity: 1,
      opacityFrom: 0.7,
      opacityTo: 0.9,
      stops: [0, 100]
    }
  },
  stroke: {
    width: 1
  },
  responsive: [{
    breakpoint: undefined,
    options: {}
  }]
}

const SocialSecurityWidget = ({
  benefits,
  persons,
  annualInf,
  applyReduction,
  totalTax,
  netLiquidAssets,
  onMaximize,
  isMaximizing
}: {
  benefits: ISocialSecurityBenefit[]
  persons: Person[]
  annualInf?: number
  applyReduction?: boolean
  totalTax?: number
  netLiquidAssets?: number
  onMaximize?(): void
  isMaximizing: boolean
}) => {
  const [options, setOptions] = useState<any>()
  const [series, setSeries] = useState<any>()
  const [lifetimeBenefit1, setLifetimeBenefit1] = useState<number>(0)
  const [lifetimeBenefit2, setLifetimeBenefit2] = useState<number>(0)
  const [infRate, setInfRate] = useState<number>(0.03)
  const [notice, setNotice] = useState<string | undefined>()

  const {calculator} = useResources()

  const theme = useTheme()
  const styles = {
    heading1: {
      color: theme.palette.primary.main,
      fontWeight: 'bold'
    },
    heading2: {
      color: theme.palette.secondary.main,
      fontWeight: 'bold'
    },
    row: {
      height: 42,
      minHeight: 42,
      maxHeight: 42,
    },
    formLabel: {
      fontSize: 12,
      fontWeight: 400,
      lineHeight: "1.0em",
      maxHeight: 15,
      marginBottom: "1px",
      textWrap: "nowrap",
      color: theme.palette.grey["700"]
    },
    notice: {
      fontSize: 12,
      fontWeight: 400,
      textIndent: "-8px",
      paddingLeft: "8px",
      marginTop: 1,
      color: theme.palette.grey["700"]
    },
    maximizeBox: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      padding: 1,
      backgroundColor: globalColors.details,
      borderColor: globalColors.details,
      borderRadius: "10px",
    }
  }

  useEffect(() => {
    calculateData()
  }, [benefits, persons, annualInf, applyReduction, totalTax, netLiquidAssets])

  const calculateData = () => {
    if (persons.length > 0 && benefits.length > 0) {
      const newOptions = cloneDeep(defaultOptions)
      const dates = []
      const person1 = persons[0]
      const benefits1 = benefits[0]
      if (benefits1.personalStart) {
        dates.push(benefits1.personalStart)
      }
      if (benefits1.spousalStart) {
        dates.push(benefits1.spousalStart)
      }
      let person2 = persons.length > 1 ? persons[1] : undefined
      let benefits2 = benefits.length > 1 ? benefits[1] : undefined
      if (benefits2) {
        if (benefits2.personalStart) {
          dates.push(benefits2.personalStart)
        }
        if (benefits2.spousalStart) {
          dates.push(benefits2.spousalStart)
        }
      }

      const minIsoDate = dates.reduce((accum: string, date: string) => accum.localeCompare(date) <= 0 ? accum : date, "2999-12-31")
      const today = startOfToday()
      let minDate = isoToLocalDate(minIsoDate)
      if (minDate.getFullYear() < today.getFullYear()) {
        // Don't start in the past
        minDate = startOfYear(today)
        setNotice("* Previously paid benefits are not included in Lifetime Benefits")
      } else {
        setNotice(undefined)
      }
      minDate = addYears(minDate, -1)
      let totalBenefit1 = 0
      let totalBenefit2 = 0

      if (minDate) {
        newOptions.xaxis.min = minDate.getTime()
      }
      const maxDate = addYears(isoToLocalDate(person1.latestLifeExpectancyDate), 1)
      newOptions.xaxis.max = maxDate.getTime()

      const dataSeries: {name:string, color: string, data:any[]}[] = []
      let data : [number, number, number?][] = []

      if (benefits1.personalStart) {
        data.push(getDataValue(benefits1.personalStart, 0))
        if (benefits1.spousalStart) {
          const personalData = getDateRangeValues(benefits1.personalStart, priorMonth(benefits1.spousalStart), benefits1.personalBenefit)
          let spousalData: [number, number, number][] = []
          let survivorData: [number, number, number][] = []
          if (person2 && person1.lifeExpectancyDate > person2.lifeExpectancyDate && benefits2 && benefits2.personalBenefit > benefits1.personalBenefit) {
            // Take the spouses greater benefit if live longer
            spousalData = getDateRangeValues(benefits1.spousalStart, person2.lifeExpectancyDate, benefits1.spousalBenefit)
            survivorData = getDateRangeValues(nextDay(person2.lifeExpectancyDate), person1.lifeExpectancyDate, benefits2.personalBenefit)
          } else {
            spousalData = getDateRangeValues(benefits1.spousalStart, person1.lifeExpectancyDate, benefits1.spousalBenefit)
          }
          data = [...data, ...personalData, ...spousalData, ...survivorData]
        } else {
          if (person2 && person1.lifeExpectancyDate > person2.lifeExpectancyDate && benefits2 && benefits2.personalBenefit > benefits1.personalBenefit) {
            // Take the spouses greater benefit if live longer
            const personalData = getDateRangeValues(benefits1.personalStart, person2.lifeExpectancyDate, benefits1.personalBenefit)
            const spouseData = getDateRangeValues(nextDay(person2.lifeExpectancyDate), person1.lifeExpectancyDate, benefits2.personalBenefit)
            data = [...data, ...personalData, ...spouseData]
          } else {
            const personalData = getDateRangeValues(benefits1.personalStart, person1.lifeExpectancyDate, benefits1.personalBenefit)
            data = [...data, ...personalData]
          }
        }
      } else if (benefits1.spousalStart) {
        data.push(getDataValue(benefits1.spousalStart, 0))
        let spousalData: [number, number, number][] = []
        let survivorData: [number, number, number][] = []
        if (person2 && person1.lifeExpectancyDate > person2.lifeExpectancyDate && benefits2 && benefits2.personalBenefit > benefits1.personalBenefit) {
          // Take the spouses greater benefit if live longer
          spousalData = getDateRangeValues(benefits1.spousalStart, person2.lifeExpectancyDate, benefits1.spousalBenefit)
          survivorData = getDateRangeValues(nextDay(person2.lifeExpectancyDate), person1.lifeExpectancyDate, benefits2.personalBenefit)
        } else {
          spousalData = getDateRangeValues(benefits1.spousalStart, person1.lifeExpectancyDate, benefits1.spousalBenefit)
        }
        data = [...data, ...spousalData, ...survivorData]
      }
      data.push(getDataValue(person1.lifeExpectancyDate, 0))
      dataSeries.push({name: `${person1.nickname}'s Social Security Benefit`, color:globalColors.primary, data: data})
      totalBenefit1 = data.reduce((accum: number, datum: [number, number, number?]) => datum[2] ? accum + datum[2] : accum, 0)
      setLifetimeBenefit1(totalBenefit1)
      // console.debug(`${person1.nickname}'s Total Benefit: ${numberToMoneyFormat(totalBenefit1, 0)}`)

      if (benefits2 && person2) {
        data = []
        if (benefits2.personalStart) {
          data.push(getDataValue(benefits2.personalStart, 0))
          if (benefits2.spousalStart) {
            const personalData = getDateRangeValues(benefits2.personalStart, priorMonth(benefits2.spousalStart), benefits2.personalBenefit)
            let spousalData: [number, number, number][] = []
            let survivorData: [number, number, number][] = []
            if (person2.lifeExpectancyDate > person1.lifeExpectancyDate && benefits1.personalBenefit > benefits2.personalBenefit) {
              // Take the spouses greater benefit if live longer
              spousalData = getDateRangeValues(benefits2.spousalStart, person1.lifeExpectancyDate, benefits2.spousalBenefit)
              survivorData = getDateRangeValues(nextDay(person1.lifeExpectancyDate), person2.lifeExpectancyDate, benefits1.personalBenefit)
            } else {
              spousalData = getDateRangeValues(benefits2.spousalStart, person2.lifeExpectancyDate, benefits2.spousalBenefit)
            }
            data = [...data, ...personalData, ...spousalData, ...survivorData]
          } else {
            if (person2.lifeExpectancyDate > person1.lifeExpectancyDate && benefits1.personalBenefit > benefits2.personalBenefit) {
              // Take the spouses greater benefit if live longer
              const personalData = getDateRangeValues(benefits2.personalStart, person1.lifeExpectancyDate, benefits2.personalBenefit)
              const spouseData = getDateRangeValues(nextDay(person1.lifeExpectancyDate), person2.lifeExpectancyDate, benefits1.personalBenefit)
              data = [...data, ...personalData, ...spouseData]
            } else {
              const personalData = getDateRangeValues(benefits2.personalStart, person2.lifeExpectancyDate, benefits2.personalBenefit)
              data = [...data, ...personalData]
            }
          }
        } else if (benefits2.spousalStart) {
          data.push(getDataValue(benefits2.spousalStart, 0))
          let spousalData: [number, number, number][] = []
          let survivorData: [number, number, number][] = []
          if (person2.lifeExpectancyDate > person1.lifeExpectancyDate && benefits1.personalBenefit > benefits2.personalBenefit) {
            // Take the spouses greater benefit if live longer
            spousalData = getDateRangeValues(benefits2.spousalStart, person1.lifeExpectancyDate, benefits2.spousalBenefit)
            survivorData = getDateRangeValues(nextDay(person1.lifeExpectancyDate), person2.lifeExpectancyDate, benefits1.personalBenefit)
          } else {
            spousalData = getDateRangeValues(benefits2.spousalStart, person2.lifeExpectancyDate, benefits2.spousalBenefit)
          }
          data = [...data, ...spousalData, ...survivorData]
        }
        data.push(getDataValue(person2.lifeExpectancyDate, 0))
        dataSeries.push({name: `${person2.nickname}'s Social Security Benefit`, color:globalColors.secondary, data: data})
        totalBenefit2 = data.reduce((accum: number, datum: [number, number, number?]) => datum[2] ? accum + datum[2] : accum, 0)
        setLifetimeBenefit2(totalBenefit2)
        // console.debug(`${person2.nickname}'s Total Benefit: ${numberToMoneyFormat(totalBenefit2, 0)}`)
      }

      setInfRate(annualInf !== undefined ? annualInf : 0.03)

      const maxValue = data.reduce((accum: number, datum: [number, number, number?]) => datum[1] > accum ? datum[1] : accum, 0)
      // console.debug(`maxValue = ${numberToMoneyFormat(maxValue, 0)}`)
      newOptions.yaxis.max = Math.round(maxValue / 1000) * 1000 + 1000
      // console.debug(`yaxis.max = ${numberToMoneyFormat(newOptions.yaxis.max, 0)}`)

      setOptions(newOptions)
      setSeries(dataSeries)

      return(totalBenefit1 + totalBenefit2)
    }
  }

  const getInfRate = (year: number): number => {
    let rate = 0
    if (applyReduction) {
      rate = SocialSecurityStrategyChange.reductionSchedule.get(year) ?? annualInf ?? 0
    } else if (annualInf) {
      rate = annualInf
    }
    return rate
  }

  const getDataValue = (isoDate: string, presentValue: number) : [number, number] => {
    let futureValue = 0
    let date = isoToLocalDate(isoDate)
    const today = getDateToday()
    if (date.getFullYear() < today.getFullYear()) {
      // Move start up to start of current year if it is in the past
      date = startOfYear(today)
    }

    if (presentValue > 0) {
      const months = differenceInMonths(date, today)
      const rate = getInfRate(date.getFullYear()) / 12
      futureValue = calculator.futureValue(presentValue, rate, months)
    }
    return [date.getTime(), futureValue]
  }

  const getDateRangeValues = (start: string, end: string, presentValue: number) : [number, number, number][] => {
    // console.debug(`${start} - ${end}: ${numberToMoneyFormat(presentValue, 0)}`)
    const values: [number, number, number][] = []
    const today = getDateToday()
    let date = isoToLocalDate(start)
    if (date.getFullYear() < today.getFullYear()) {
      // Move start up to start of current year if it is in the past
      date = startOfYear(today)
    }
    const endDate = isoToLocalDate(end)
    let rate = 0

    // Get value at start of range
    let thisYear = today.getFullYear()
    let startYear = date.getFullYear()
    let value = presentValue
    // let years = differenceInCalendarYears(date, today)
    let year: number
    for (year = thisYear + 1; year <= startYear; year++) {
      rate = getInfRate(year)
      value += value * rate
    }
    values.push([date.getTime(), value, 0])

    // Compute yearly intermediate points
    while (date.getFullYear() < endDate.getFullYear()) {
      let nextDate = startOfYear(addYears(date, 1))
      let months = differenceInCalendarMonths(nextDate, date)
      let benefit = Math.round(months * value)
      // console.debug(`${date.getFullYear()}: ${numberToMoneyFormat(value, 0)} x ${months}mos = ${numberToMoneyFormat(benefit, 0)}`)
      values.push([nextDate.getTime(), value, benefit])
      // Get start of next year and new value
      // nextDate = startOfDay(addDays(nextDate, 1))
      ++year
      rate = getInfRate(nextDate.getFullYear())
      value += value * rate
      values.push([nextDate.getTime(), value, 0])
      date = nextDate
    }

    // Handle remaining months
    const months = differenceInCalendarMonths(endDate, date) + 1
    if (months > 0) {
      let benefit = months * value
      // console.debug(`${endDate.getFullYear()}: ${numberToMoneyFormat(value, 0)} x ${months}mos = ${numberToMoneyFormat(benefit, 0)}`)
      values.push([endDate.getTime(), value, benefit])
    }

    return values
  }

  const nextDay = (isoDate: string) => {
    const date = addDays(isoToLocalDate(isoDate), 1)
    return getISODateFromDate(date)
  }

  const priorMonth = (isoDate: string) => {
    const date = endOfMonth(addMonths(isoToLocalDate(isoDate), -1))
    return getISODateFromDate(date)
  }

  const handleMaximize = () => {
    if (onMaximize) {
      onMaximize()
    }
  }

  if (options && series) {
    return (
      <Grid container spacing={2} direction="row">
        <Grid item xs={12} sm={8}>
          <Chart
            options={options}
            series={series}
            type="area"
            height="250"
            width="565"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Grid container>
            <Grid item xs={6}>
              {benefits.length > 0 && persons.length > 0 &&
                <Grid container spacing={1} direction="column" sx={{mt:1}}>
                  <Grid item>
                    <Typography variant="body2" style={styles.heading1}>{persons[0].nickname}</Typography>
                  </Grid>
                  <Grid item sx={styles.row}>
                    <FormLabel sx={styles.formLabel}>Personal Benefit {benefits[0].personalStart && benefits[0].personalPercent ? numberToPercentFormat(benefits[0].personalPercent, 0) : ""}</FormLabel>
                    {benefits[0].personalStart &&
                      <Typography variant="body2">{numberToMoneyFormat(benefits[0].personalBenefit, 0)}</Typography>
                    }
                  </Grid>
                  {benefits.length > 1 &&
                    <Grid item sx={styles.row}>
                      <FormLabel sx={styles.formLabel}>Spousal Benefit {benefits[0].spousalStart && benefits[0].spousalPercent ? numberToPercentFormat(benefits[0].spousalPercent, 0) : ""}</FormLabel>
                      {benefits[0].spousalStart &&
                        <Typography variant="body2">{numberToMoneyFormat(benefits[0].spousalBenefit, 0)}</Typography>
                      }
                    </Grid>
                  }
                  <Grid item sx={styles.row}>
                    <FormLabel sx={styles.formLabel}>Lifetime Benefit</FormLabel>
                    <Typography variant="body2">{numberToMoneyFormat(lifetimeBenefit1, 0)}</Typography>
                  </Grid>
                  <Grid item sx={styles.row}>
                    <FormLabel sx={styles.formLabel}>Lifetime Taxes</FormLabel>
                    <Typography variant="body2">{totalTax ? numberToMoneyFormat(totalTax, 0) : "$0"}</Typography>
                  </Grid>
                  <Grid item sx={styles.row}>
                    <FormLabel sx={styles.formLabel}>Net Liquid Assets</FormLabel>
                    <Typography variant="body2">{netLiquidAssets ? numberToMoneyFormat(netLiquidAssets, 0) : "$0"}</Typography>
                  </Grid>
                </Grid>
              }
            </Grid>
            <Grid item xs={6}>
              {benefits.length > 1 && persons.length > 1 &&
                <Grid container spacing={1} direction="column" sx={{mt:1}}>
                  <Grid item>
                    <Typography variant="body2" style={styles.heading2}>{persons[1].nickname}</Typography>
                  </Grid>
                  <Grid item sx={styles.row}>
                    <FormLabel sx={styles.formLabel}>Personal Benefit {benefits[1].personalStart && benefits[1].personalPercent ? numberToPercentFormat(benefits[1].personalPercent, 0) : ""}</FormLabel>
                    {benefits[1].personalStart &&
                      <Typography variant="body2">{numberToMoneyFormat(benefits[1].personalBenefit, 0)}</Typography>
                    }
                  </Grid>
                  <Grid item sx={styles.row}>
                    <FormLabel sx={styles.formLabel}>Spousal Benefit {benefits[1].spousalStart && benefits[1].spousalPercent ? numberToPercentFormat(benefits[1].spousalPercent, 0) : ""}</FormLabel>
                    {benefits[1].spousalStart &&
                      <Typography variant="body2">{numberToMoneyFormat(benefits[1].spousalBenefit, 0)}</Typography>
                    }
                  </Grid>
                  <Grid item sx={styles.row}>
                    <FormLabel sx={styles.formLabel}>Lifetime Benefit</FormLabel>
                    <Typography variant="body2">{numberToMoneyFormat(lifetimeBenefit2, 0)}</Typography>
                  </Grid>
                  <Grid item sx={styles.row}>
                    <Box sx={styles.maximizeBox}>
                      <FormLabel sx={styles.formLabel}><b>Total Benefit</b></FormLabel>
                      <Typography variant="body2"><b>{numberToMoneyFormat(lifetimeBenefit1 + lifetimeBenefit2, 0)}</b></Typography>
                      <ProgressButton variant="contained" size="small" color="primary" processing={isMaximizing}
                                      onClick={handleMaximize}>Maximize</ProgressButton>
                    </Box>
                  </Grid>
                </Grid>
              }
              {benefits.length === 1 && persons.length === 1 &&
                <Grid container spacing={1} direction="column" sx={{mt:1}}>
                  <Grid item sx={{display:"flex", flexDirection:"column", justifyContent:"flex-end", height:108, width:90}}>
                    <ProgressButton variant="contained" size="small" color="primary" processing={isMaximizing}
                                    onClick={handleMaximize}>Maximize</ProgressButton>
                  </Grid>
                </Grid>
              }
            </Grid>
            {notice &&
              <Grid item xs={12}>
                <Typography variant="body2" sx={styles.notice}>{notice}</Typography>
              </Grid>
            }
          </Grid>
        </Grid>
      </Grid>
    )
  } else {
    return null
  }
}

export default SocialSecurityWidget